import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { isNative, WEB_ORIGIN, redirectExternal } from "@/lib/native";
import { ArrowLeft, FileText } from "lucide-react";
import { money } from "@/components/marketplace/shared";
import { L } from "@/pages/marketplace/Sell";
import { PlansLink } from "@/components/marketplace/PlansLink";
import { useTour } from "@/components/Onboarding";
import { MessageButton } from "@/components/messages/shared";

export const QUOTE_LABEL = { pending: "Pendente", quoted: "Proposta enviada", accepted: "Aceite — aguarda pagamento", paid: "Pago", rejected: "Recusado", expired: "Expirado", cancelled: "Cancelado" };
const TONE = { pending: "bg-yellow-100 text-yellow-800", quoted: "bg-blue-100 text-blue-800", accepted: "bg-orange-100 text-orange-800", paid: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700", expired: "bg-zinc-200 text-zinc-700", cancelled: "bg-zinc-200 text-zinc-700" };
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("pt-PT") : "—";
const inputCls = "w-full px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black";

export function QuotePill({ status }) { return <span data-testid={`quote-status-${status}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${TONE[status] || "bg-zinc-100"}`}>{QUOTE_LABEL[status] || status}</span>; }

export function QuoteCard({ q, who, children }) {
  return (
    <div data-testid={`quote-${q.id}`} className="bg-white rounded-2xl border border-zinc-200 p-3 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div><div className="font-semibold text-sm">{q.service_title}</div><div className="text-zinc-500">{who === "buyer" ? q.seller_name : (q.buyer_name || q.buyer_email)} · {fmt(q.created_at)}</div></div>
        <QuotePill status={q.status} />
      </div>
      <p className="mt-2 text-zinc-700 whitespace-pre-line">{q.request_details}</p>
      <div className="mt-2"><MessageButton context_type="quote" context_id={q.id} label={who === "buyer" ? "Mensagem ao prestador" : "Mensagem ao cliente"} /></div>
      <div className="flex flex-wrap gap-x-3 mt-1 text-zinc-500">
        {q.quantity && <span>Qtd: {q.quantity}</span>}{q.requested_date && <span>Data: {q.requested_date}</span>}{q.location && <span>Local: {q.location}</span>}{q.budget_minor != null && <span>Orçamento indicativo: {money(q.budget_minor, q.currency)}</span>}
      </div>
      {q.quoted_amount_minor != null && (
        <div data-testid={`quote-proposal-${q.id}`} className="mt-2 p-2.5 rounded-xl bg-zinc-50 border border-zinc-200">
          <div className="flex items-center justify-between"><span className="text-zinc-500">Proposta</span><span className="font-display font-extrabold text-base">{money(q.quoted_amount_minor, q.currency)}</span></div>
          {q.seller_message && <p className="mt-1 text-zinc-700">{q.seller_message}</p>}{q.conditions && <p className="text-zinc-500 mt-0.5">{q.conditions}</p>}
          <div className="text-zinc-500 mt-1">Validade: {fmt(q.proposal_expires_at)}{q.paid_at && ` · Pago em ${fmt(q.paid_at)}`}</div>
        </div>
      )}
      {children}
    </div>
  );
}

const BUYER_TABS = [["pending", "Pendentes", ["pending"]], ["quoted", "Propostas", ["quoted"]], ["accepted", "Aceites", ["accepted"]], ["paid", "Pagos", ["paid"]], ["closed", "Expirados/Recusados", ["expired", "rejected", "cancelled"]]];

export default function MyQuotes() {
  const { confirm } = useConfirm();
  useTour("buyer_quotes");
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(null);
  const [link, setLink] = useState(null);
  const load = () => api.get("/quotes/mine").then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const accept = async (q) => {
    setBusy(q.id);
    try {
      const { data } = await api.post(`/quotes/${q.id}/accept`, { origin_url: isNative ? WEB_ORIGIN : window.location.origin });
      const how = await redirectExternal(data.checkout_url);
      if (how === "blocked" || how === "popup") setLink(data.checkout_url);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
    finally { setTimeout(() => setBusy(null), 3000); }
  };
  const act = async (q, action) => {
    if (action === "reject" && !(await confirm({ title: "Recusar esta proposta?", confirmLabel: "Recusar" }))) return;
    try { await api.post(`/quotes/${q.id}/${action}`); toast.success(action === "reject" ? "Proposta recusada" : "Pedido cancelado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const visible = items?.filter(q => tab === "all" || BUYER_TABS.find(t => t[0] === tab)[2].includes(q.status));
  return (
    <div className="page fade-up">
      <Link to="/marketplace/servicos" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Serviços</Link>
      <h1 className="font-display text-2xl font-extrabold mb-3">Pedidos de orçamento</h1>
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-3">
        <button onClick={() => setTab("all")} className="chip whitespace-nowrap" data-active={tab === "all"}>Todos</button>
        {BUYER_TABS.map(([k, l]) => <button key={k} data-testid={`myquotes-tab-${k}`} onClick={() => setTab(k)} className="chip whitespace-nowrap" data-active={tab === k}>{l}</button>)}
      </div>
      {link && <a data-testid="quote-pay-link" href={link} target="_blank" rel="noopener noreferrer" className="block text-center text-xs font-semibold underline text-[#c8102e] mb-3">O pagamento abriu numa nova janela. Se não apareceu, toca aqui.</a>}
      {visible?.length === 0 && <div data-testid="myquotes-empty" className="text-center py-12 text-zinc-500 text-sm"><FileText size={28} className="mx-auto mb-2 text-zinc-300" />Sem pedidos de orçamento.</div>}
      <div className="space-y-2 mb-8">
        {visible?.map(q => (
          <QuoteCard key={q.id} q={q} who="buyer">
            {q.status === "quoted" && <div className="grid grid-cols-2 gap-2 mt-2">
              <button data-testid={`quote-accept-${q.id}`} onClick={() => accept(q)} disabled={busy === q.id} className="py-2.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white font-semibold disabled:opacity-50">{busy === q.id ? "A abrir Stripe…" : "Aceitar e pagar"}</button>
              <button data-testid={`quote-reject-${q.id}`} onClick={() => act(q, "reject")} className="py-2.5 rounded-xl border border-zinc-300 font-semibold hover:border-red-500 hover:text-red-700">Recusar</button>
            </div>}
            {q.status === "accepted" && <div className="mt-2 space-y-2">
              <p data-testid={`quote-accepted-notice-${q.id}`} className="text-orange-800 bg-orange-50 border border-orange-200 rounded-lg p-2">Proposta aceite. O serviço só fica confirmado depois do pagamento.</p>
              <button data-testid={`quote-pay-${q.id}`} onClick={() => accept(q)} disabled={busy === q.id} className="w-full py-2.5 rounded-xl bg-black hover:bg-[#c8102e] text-white font-semibold disabled:opacity-50">{busy === q.id ? "A abrir Stripe…" : `Pagar ${money(q.quoted_amount_minor, q.currency)}`}</button>
            </div>}
            {q.status === "pending" && <button data-testid={`quote-cancel-${q.id}`} onClick={() => act(q, "cancel")} className="mt-2 text-zinc-500 underline">Cancelar pedido</button>}
            {q.status === "paid" && <Link to="/marketplace/compras" className="block mt-2 text-center py-2 rounded-lg bg-green-50 border border-green-200 text-green-800 font-semibold">Serviço confirmado — ver encomenda</Link>}
          </QuoteCard>
        ))}
      </div>
    </div>
  );
}

const SELLER_TABS = [["pending", "Recebidos", ["pending"]], ["quoted", "Respondidos", ["quoted"]], ["accepted", "Aceites", ["accepted"]], ["paid", "Concluídos", ["paid"]], ["closed", "Rejeitados/Expirados", ["rejected", "expired", "cancelled"]]];

export function SellerQuotes({ locked }) {
  const { confirm } = useConfirm();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/quotes/received").then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const reject = async (q) => {
    if (!(await confirm({ title: "Recusar este pedido?", confirmLabel: "Recusar" }))) return;
    try { await api.post(`/quotes/${q.id}/reject`); toast.success("Pedido recusado"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const visible = items.filter(q => SELLER_TABS.find(t => t[0] === tab)[2].includes(q.status));
  return (
    <div>
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-3">
        {SELLER_TABS.map(([k, l, sts]) => <button key={k} data-testid={`seller-quotes-tab-${k}`} onClick={() => setTab(k)} className="chip whitespace-nowrap" data-active={tab === k}>{l} ({items.filter(q => sts.includes(q.status)).length})</button>)}
      </div>
      {visible.length === 0 && <p data-testid="seller-quotes-empty" className="text-sm text-zinc-500 text-center py-8">Sem pedidos neste separador.</p>}
      <div className="space-y-2 mb-8">
        {visible.map(q => (
          <QuoteCard key={q.id} q={q} who="seller">
            {q.contact && <div className="text-zinc-500 mt-1">Contacto: {q.contact}</div>}
            {q.status === "pending" && (locked ? <PlansLink testid={`quote-plans-${q.id}`} label="Ver planos para responder" /> : (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button data-testid={`quote-propose-${q.id}`} onClick={() => setOpen(q)} className="py-2.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white font-semibold">Enviar proposta</button>
                <button data-testid={`quote-seller-reject-${q.id}`} onClick={() => reject(q)} className="py-2.5 rounded-xl border border-zinc-300 font-semibold hover:border-red-500">Recusar</button>
              </div>))}
            {q.status === "accepted" && <p className="mt-2 text-orange-800 bg-orange-50 border border-orange-200 rounded-lg p-2">Cliente aceitou — a aguardar pagamento. Só recebes após confirmação do Stripe.</p>}
            {q.status === "paid" && <p className="mt-2 text-green-800 bg-green-50 border border-green-200 rounded-lg p-2">Pago. Gere o serviço em «Minhas vendas».</p>}
          </QuoteCard>
        ))}
      </div>
      {open && <ProposalForm q={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function ProposalForm({ q, onClose, onDone }) {
  const d = new Date(); d.setDate(d.getDate() + 7);
  const [f, setF] = useState({ amount: "", expires: d.toISOString().slice(0, 10), message: "", conditions: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/quotes/${q.id}/propose`, { quoted_amount_minor: Math.round(parseFloat(f.amount) * 100), proposal_expires_at: new Date(f.expires + "T23:59:59Z").toISOString(), seller_message: f.message, conditions: f.conditions || null });
      toast.success("Proposta enviada"); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-3 pb-24 sm:pb-3" onClick={onClose}>
      <form data-testid="proposal-form" onSubmit={send} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-4 space-y-3 max-h-[72vh] overflow-y-auto">
        <div className="font-display text-lg font-extrabold">Enviar proposta</div>
        <div className="text-xs text-zinc-600">{q.service_title} · {q.buyer_name || q.buyer_email}</div>
        <div className="grid grid-cols-2 gap-2">
          <L label="Preço (£) *"><input data-testid="proposal-amount" required type="number" step="0.01" min="0.01" className={inputCls} value={f.amount} onChange={e => set("amount", e.target.value)} /></L>
          <L label="Validade *"><input data-testid="proposal-expires" required type="date" className={inputCls} value={f.expires} onChange={e => set("expires", e.target.value)} /></L>
        </div>
        <L label="Mensagem *"><textarea data-testid="proposal-message" required rows={3} className={inputCls} value={f.message} onChange={e => set("message", e.target.value)} placeholder="O que inclui a proposta" /></L>
        <L label="Condições / notas"><input className={inputCls} value={f.conditions} onChange={e => set("conditions", e.target.value)} /></L>
        <p className="text-[11px] text-zinc-500">Comissão ANGOLAUK+ 10% sobre o valor pago. Recebes o líquido via Stripe Connect.</p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onClose} className="py-3 rounded-xl border border-zinc-300 text-sm font-semibold">Cancelar</button>
          <button data-testid="proposal-submit" disabled={busy} className="py-3 rounded-xl bg-[#c8102e] text-white text-sm font-semibold disabled:opacity-50">{busy ? "A enviar…" : "Enviar proposta"}</button>
        </div>
      </form>
    </div>
  );
}
